import { ConditionAnswerSchema, type ConditionAnswer } from '@bk/contracts';

export interface StoredAnswer { id: string; key: string; body: ConditionAnswer; createdAt: number }
export interface Snapshot { pending: readonly StoredAnswer[]; hidden: ReadonlySet<string>; error: string | null; revision: number }
export const answerIdentity = (key: string, sede?: string) => `${sede ?? 'azienda'}:${key}`;

/** Una voce di storage per risposta: il refresh e un'altra scheda non possono cancellare una coda tenuta solo in memoria. */
export class ConditionAnswers {
  readonly prefix: string;
  private readonly listeners = new Set<() => void>();
  private readonly confirmed = new Set<string>();
  private users = 0;
  private running = false;
  private timer?: ReturnType<typeof setTimeout>;
  private error: string | null = null;
  private snapshot: Snapshot = { pending: [], hidden: new Set(), error: null, revision: 0 };

  constructor(
    private readonly storage: Storage,
    userId: string,
    tenantId: string,
    private readonly send: (key: string, body: ConditionAnswer) => Promise<unknown>,
    private readonly exclusive: (work: () => Promise<void>) => Promise<void> = (work) => work(),
  ) {
    this.prefix = `bk.condition-answers:${userId}:${tenantId}:`;
    this.reload();
  }

  getSnapshot = (): Snapshot => this.snapshot;
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  retain(): () => void {
    this.users++;
    this.retry();
    return () => {
      this.users--;
      if (!this.users) clearTimeout(this.timer);
    };
  }

  /** Il chiamante avanza solo dopo la scrittura locale sincrona, senza attendere rete né ricomposizione. */
  answer(key: string, body: ConditionAnswer): void {
    const entry: StoredAnswer = { id: crypto.randomUUID(), key, body, createdAt: Date.now() };
    this.storage.setItem(this.prefix + entry.id, JSON.stringify(entry));
    this.error = null;
    this.reload();
    void this.flush();
  }

  private read(): StoredAnswer[] {
    const entries: StoredAnswer[] = [];
    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i);
      if (!key?.startsWith(this.prefix)) continue;
      const raw = this.storage.getItem(key);
      if (!raw) continue;
      const entry = JSON.parse(raw) as StoredAnswer;
      if (typeof entry.id !== 'string' || key !== this.prefix + entry.id || typeof entry.key !== 'string' || !Number.isFinite(entry.createdAt) || !ConditionAnswerSchema.safeParse(entry.body).success) throw new Error('Una risposta conservata nel browser non è leggibile.');
      entries.push(entry);
    }
    return entries.sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id));
  }

  reload = (): void => {
    let pending = this.snapshot.pending;
    try { pending = this.read(); } catch (error) { this.error = error instanceof Error ? error.message : String(error); }
    this.snapshot = { pending, hidden: new Set([...this.confirmed, ...pending.map((x) => answerIdentity(x.key, x.body.sede))]), error: this.error, revision: this.snapshot.revision + 1 };
    for (const listener of this.listeners) listener();
  };

  retry = (): void => {
    clearTimeout(this.timer);
    this.error = null;
    this.reload();
    void this.flush();
  };

  private async flush(): Promise<void> {
    if (this.running || !this.users) return;
    this.running = true;
    try {
      await this.exclusive(async () => {
        while (this.users) {
          const entry = this.read()[0];
          if (!entry) break;
          await this.send(entry.key, entry.body);
          // Si elimina soltanto la voce confermata: altre schede possono averne aggiunte nel frattempo.
          this.storage.removeItem(this.prefix + entry.id);
          this.confirmed.add(answerIdentity(entry.key, entry.body.sede));
          this.error = null;
          this.reload();
        }
      });
    } catch (error) {
      this.error = error instanceof Error ? error.message : String(error);
      this.reload();
      if (this.users) this.timer = setTimeout(this.retry, 10_000);
    } finally {
      this.running = false;
      // Una risposta può essere stata aggiunta tra l'ultima lettura e il rilascio del lock.
      if (this.users && !this.error && this.snapshot.pending.length) void this.flush();
    }
  }
}
